import { FormTypes } from '@oneblink/types'
import memoize from 'lodash.memoize'

type Submission = Record<string, unknown>

// species

const cattle = (submission: Submission) =>
  Array.isArray(submission.Cattle_Tick_Carrier) &&
  submission.Cattle_Tick_Carrier.includes('Cattle')

const buffaloBisonAndDeer = (submission: Submission) =>
  Array.isArray(submission.Cattle_Tick_Carrier) &&
  (submission.Cattle_Tick_Carrier.includes('Bison') ||
    submission.Cattle_Tick_Carrier.includes('Buffalo') ||
    submission.Cattle_Tick_Carrier.includes('Deer'))

const equine = (submission: Submission) =>
  Array.isArray(submission.Cattle_Tick_Carrier) &&
  submission.Cattle_Tick_Carrier.includes('Equine')

const horsesOnly = (submission: Submission) =>
  Array.isArray(submission.Cattle_Tick_Carrier) &&
  submission.Cattle_Tick_Carrier.join() === 'Equine' &&
  Array.isArray(submission.Species) &&
  submission.Species.join() === 'Horse'

const sheepGoatsAndCamelids = (submission: Submission) =>
  Array.isArray(submission.Cattle_Tick_Carrier) &&
  (submission.Cattle_Tick_Carrier.includes('Sheep') ||
    submission.Cattle_Tick_Carrier.includes('Goat') ||
    submission.Cattle_Tick_Carrier.includes('Camelids'))

const anySpecies = (submission: Submission) =>
  cattle(submission) ||
  buffaloBisonAndDeer(submission) ||
  equine(submission) ||
  sheepGoatsAndCamelids(submission)

// entering NSW - origin

const qldInfestedZone = (submission: Submission) =>
  submission.Movement_Originating === 'FROM the QLD infested zone'

const qldTickFreeZone = (submission: Submission) =>
  submission.Movement_Originating ===
  'FROM a non-restricted property in the QLD tick free zone'

const qldRestrictedProperty = (submission: Submission) =>
  submission.Movement_Originating ===
  'FROM a restricted property in the QLD tick free zone'

const magicMillions = (submission: Submission) =>
  submission.Movement_Originating === 'FROM the Magic Millions Sales Complex'

const ekka = (submission: Submission) =>
  submission.Movement_Originating === 'FROM the Royal Brisbane Show (EKKA)'

// entering NSW - via

const viaQldInfestedZone = (submission: Submission) =>
  submission.Movement_Entering_NSW_Via === 'the QLD infested zone'

const viaQldTickFreeZone = (submission: Submission) =>
  submission.Movement_Entering_NSW_Via === 'the QLD tick free zone'

//entering NSW origin && via

const qldInfestedZoneViaQldInfestedZone = (submission: Submission) =>
  qldInfestedZone(submission) && viaQldInfestedZone(submission)

const qldInfestedZoneViaQldTickFreeZone = (submission: Submission) =>
  qldInfestedZone(submission) && viaQldTickFreeZone(submission)

const qldTickFreeZoneViaQldInfestedZone = (submission: Submission) =>
  qldTickFreeZone(submission) && viaQldInfestedZone(submission)

const qldTickFreeZoneViaQldTickFreeZone = (submission: Submission) =>
  qldTickFreeZone(submission) && viaQldTickFreeZone(submission)

const qldRestrictedPropertyViaQldInfestedZone = (submission: Submission) =>
  qldRestrictedProperty(submission) && viaQldInfestedZone(submission)

const qldRestrictedPropertyViaQldTickFreeZone = (submission: Submission) =>
  qldRestrictedProperty(submission) && viaQldTickFreeZone(submission)

const magicMillionsViaQldInfestedZone = (submission: Submission) =>
  magicMillions(submission) && viaQldInfestedZone(submission)

const magicMillionsViaQldTickFreeZone = (submission: Submission) =>
  magicMillions(submission) && viaQldTickFreeZone(submission)

const ekkaViaQldInfestedZone = (submission: Submission) =>
  ekka(submission) && viaQldInfestedZone(submission)

const ekkaViaQldTickFreeZone = (submission: Submission) =>
  ekka(submission) && viaQldTickFreeZone(submission)

const enteringNswOriginDetailsConditions = memoize((submission: Submission) => {
  const cattleBool = cattle(submission)
  const buffaloBisonAndDeerBool = buffaloBisonAndDeer(submission)
  const sheepGoatsAndCamelidsBool = sheepGoatsAndCamelids(submission)
  const horsesOnlyBool = horsesOnly(submission)
  const anySpeciesBool = anySpecies(submission)

  const qldInfestedZoneViaQldInfestedZoneBool =
    qldInfestedZoneViaQldInfestedZone(submission)
  const qldInfestedZoneViaQldTickFreeZoneBool =
    qldInfestedZoneViaQldTickFreeZone(submission)
  const qldTickFreeZoneViaQldInfestedZoneBool =
    qldTickFreeZoneViaQldInfestedZone(submission)
  const qldTickFreeZoneViaQldTickFreeZoneBool =
    qldTickFreeZoneViaQldTickFreeZone(submission)
  const qldRestrictedPropertyViaQldInfestedZoneBool =
    qldRestrictedPropertyViaQldInfestedZone(submission)
  const qldRestrictedPropertyViaQldTickFreeZoneBool =
    qldRestrictedPropertyViaQldTickFreeZone(submission)

  const qldInfestedZoneViaAnywhere =
    qldInfestedZoneViaQldInfestedZoneBool ||
    qldInfestedZoneViaQldTickFreeZoneBool

  const qldTickFreeZoneViaAnywhere =
    qldTickFreeZoneViaQldInfestedZoneBool ||
    qldTickFreeZoneViaQldTickFreeZoneBool

  const qldRestrictedPropertyViaAnywhere =
    qldRestrictedPropertyViaQldInfestedZoneBool ||
    qldRestrictedPropertyViaQldTickFreeZoneBool

  return {
    'From a registered feedlot':
      cattleBool &&
      (qldInfestedZoneViaAnywhere ||
        qldTickFreeZoneViaAnywhere ||
        qldRestrictedPropertyViaAnywhere),
    'From a community venue (e.g. showgrounds)':
      anySpeciesBool &&
      (qldInfestedZoneViaAnywhere ||
        qldTickFreeZoneViaAnywhere ||
        qldRestrictedPropertyViaAnywhere),
    'From a saleyard':
      (cattleBool || buffaloBisonAndDeerBool || sheepGoatsAndCamelidsBool) &&
      (qldInfestedZoneViaAnywhere ||
        qldTickFreeZoneViaAnywhere ||
        qldRestrictedPropertyViaAnywhere),
    'From a veterinary clinic':
      horsesOnlyBool &&
      (qldInfestedZoneViaAnywhere ||
        qldTickFreeZoneViaAnywhere ||
        qldRestrictedPropertyViaAnywhere),
    'From a property':
      anySpeciesBool &&
      (qldInfestedZoneViaAnywhere ||
        qldTickFreeZoneViaAnywhere ||
        qldRestrictedPropertyViaAnywhere),
    'From a designated race meeting under the control of a racing authority':
      horsesOnlyBool &&
      (qldInfestedZoneViaAnywhere ||
        qldTickFreeZoneViaAnywhere ||
        qldRestrictedPropertyViaAnywhere),
    'From a racing stable, to a race meeting under the control of a racing authority':
      horsesOnlyBool &&
      (qldInfestedZoneViaAnywhere ||
        qldTickFreeZoneViaAnywhere ||
        qldRestrictedPropertyViaAnywhere),
  }
}, JSON.stringify)

export function filterEnteringNSWOriginDetailsOptions(
  options: FormTypes.ChoiceElementOption[],
  submission: Submission,
) {
  if (submission.Entering_NSW !== 'Yes') {
    return options
  }

  const relevantSubmission = {
    Species: submission.Cattle_Tick_Carrier_Sub,
    Cattle_Tick_Carrier: submission.Cattle_Tick_Carrier,
    Movement_Originating: submission.Movement_Originating,
    Movement_Entering_NSW_Via: submission.Movement_Entering_NSW_Via,
  }

  const mask = enteringNswOriginDetailsConditions(relevantSubmission)

  const allowedOptions = Object.keys(mask).filter(
    (key) =>
      mask[key as keyof ReturnType<typeof enteringNswOriginDetailsConditions>],
  )
  return options.filter((opt) => allowedOptions.includes(opt.value))
}

const enteringNswDestinationConditions = memoize((submission) => {
  const cattleBool = cattle(submission)
  const buffaloBisonAndDeerBool = buffaloBisonAndDeer(submission)
  const sheepGoatsAndCamelidsBool = sheepGoatsAndCamelids(submission)
  const horsesOnlyBool = horsesOnly(submission)
  const anySpeciesBool = anySpecies(submission)

  const qldInfestedZoneViaQldInfestedZoneBool =
    qldInfestedZoneViaQldInfestedZone(submission)
  const qldInfestedZoneViaQldTickFreeZoneBool =
    qldInfestedZoneViaQldTickFreeZone(submission)
  const qldTickFreeZoneViaQldInfestedZoneBool =
    qldTickFreeZoneViaQldInfestedZone(submission)
  const qldTickFreeZoneViaQldTickFreeZoneBool =
    qldTickFreeZoneViaQldTickFreeZone(submission)
  const qldRestrictedPropertyViaQldInfestedZoneBool =
    qldRestrictedPropertyViaQldInfestedZone(submission)
  const qldRestrictedPropertyViaQldTickFreeZoneBool =
    qldRestrictedPropertyViaQldTickFreeZone(submission)

  const qldInfestedZoneViaAnywhere =
    qldInfestedZoneViaQldInfestedZoneBool ||
    qldInfestedZoneViaQldTickFreeZoneBool

  const qldTickFreeZoneViaAnywhere =
    qldTickFreeZoneViaQldInfestedZoneBool ||
    qldTickFreeZoneViaQldTickFreeZoneBool

  const qldRestrictedPropertyViaAnywhere =
    qldRestrictedPropertyViaQldInfestedZoneBool ||
    qldRestrictedPropertyViaQldTickFreeZoneBool

  const ekkaViaAnywhere =
    ekkaViaQldInfestedZone(submission) || ekkaViaQldTickFreeZone(submission)
  const magicMillionsViaAnywhere =
    magicMillionsViaQldInfestedZone(submission) ||
    magicMillionsViaQldTickFreeZone(submission)

  return {
    'To an Abattoir':
      (cattleBool || buffaloBisonAndDeerBool || sheepGoatsAndCamelidsBool) &&
      (qldInfestedZoneViaAnywhere ||
        qldTickFreeZoneViaAnywhere ||
        qldRestrictedPropertyViaAnywhere),
    'To a saleyard':
      anySpeciesBool &&
      (qldInfestedZoneViaAnywhere ||
        qldTickFreeZoneViaAnywhere ||
        qldRestrictedPropertyViaAnywhere),
    'To a registered Feedlot':
      cattleBool &&
      (qldInfestedZoneViaAnywhere ||
        qldTickFreeZoneViaAnywhere ||
        qldRestrictedPropertyViaAnywhere),
    'To a property':
      (anySpeciesBool &&
        (qldInfestedZoneViaAnywhere ||
          qldTickFreeZoneViaAnywhere ||
          qldRestrictedPropertyViaAnywhere ||
          ekkaViaAnywhere)) ||
      (horsesOnlyBool && magicMillionsViaAnywhere),
    'To a community venue (e.g. showgrounds)':
      (anySpeciesBool &&
        (qldInfestedZoneViaAnywhere ||
          qldTickFreeZoneViaAnywhere ||
          qldRestrictedPropertyViaAnywhere)) ||
      (horsesOnlyBool && magicMillionsViaAnywhere),
    'To an agricultural show only':
      anySpeciesBool &&
      (qldInfestedZoneViaAnywhere ||
        qldTickFreeZoneViaAnywhere ||
        qldRestrictedPropertyViaAnywhere),
    'To a horse only competition':
      horsesOnlyBool &&
      (qldInfestedZoneViaAnywhere ||
        qldTickFreeZoneViaAnywhere ||
        qldRestrictedPropertyViaAnywhere),
    'To a designated race meeting under the control of a racing authority':
      horsesOnlyBool &&
      (qldInfestedZoneViaAnywhere ||
        qldTickFreeZoneViaAnywhere ||
        qldRestrictedPropertyViaAnywhere ||
        magicMillionsViaAnywhere),
    'To a racing stable, returning from a race meeting under the control of a racing authority':
      horsesOnlyBool &&
      (qldInfestedZoneViaAnywhere ||
        qldTickFreeZoneViaAnywhere ||
        qldRestrictedPropertyViaAnywhere ||
        magicMillionsViaAnywhere),
  }
}, JSON.stringify)

export function filterEnteringNSWDestinationOptions(
  options: FormTypes.ChoiceElementOption[],
  submission: Submission,
) {
  if (submission.Entering_NSW !== 'Yes') {
    return options
  }

  const relevantSubmission = {
    Species: submission.Cattle_Tick_Carrier_Sub,
    Cattle_Tick_Carrier: submission.Cattle_Tick_Carrier,
    Movement_Originating: submission.Movement_Originating,
    Movement_Entering_NSW_Via: submission.Movement_Entering_NSW_Via,
  }

  const mask = enteringNswDestinationConditions(relevantSubmission)

  const allowedOptions = Object.keys(mask).filter(
    (key) =>
      mask[key as keyof ReturnType<typeof enteringNswDestinationConditions>],
  )
  return options.filter((opt) => allowedOptions.includes(opt.value))
}

const nswInfestedHolding = (submission: Submission) =>
  submission.Movement_Originating === 'From an INFESTED holding in NSW'
const nswAdjoiningHolding = (submission: Submission) =>
  submission.Movement_Originating === 'From an ADJOINING holding in NSW'
const nswCommunityVenue = (submission: Submission) =>
  submission.Movement_Originating ===
  'From a community venue (e.g. showgrounds)'
const nswSaleyard = (submission: Submission) =>
  submission.Movement_Originating === 'From a saleyard'
const nswNonRestrictedProperty = (submission: Submission) =>
  submission.Movement_Originating === 'From a NON-RESTRICTED PROPERTY in NSW'

const anywhereFromWithinNsw = (submission: Submission) =>
  nswInfestedHolding(submission) ||
  nswAdjoiningHolding(submission) ||
  nswCommunityVenue(submission) ||
  nswSaleyard(submission) ||
  nswNonRestrictedProperty(submission)

const withinNswDestinationConditions = memoize((submission) => {
  const anySpeciesBool = anySpecies(submission)
  const nswInfestedHoldingBool = nswInfestedHolding(submission)
  const nswAdjoiningHoldingBool = nswAdjoiningHolding(submission)

  return {
    'To an Abattoir':
      anySpeciesBool && (nswInfestedHoldingBool || nswAdjoiningHoldingBool),
    'To a saleyard':
      anySpeciesBool && (nswInfestedHoldingBool || nswAdjoiningHoldingBool),
    'To a property': anySpeciesBool && anywhereFromWithinNsw(submission),
    'To a community venue (e.g. showgrounds)':
      anySpeciesBool && (nswInfestedHoldingBool || nswAdjoiningHoldingBool),
  }
}, JSON.stringify)

export function filterWithinNSWDestinationOptions(
  options: FormTypes.ChoiceElementOption[],
  submission: Submission,
) {
  if (submission.Entering_NSW !== 'No') {
    return options
  }

  const relevantSubmission = {
    Cattle_Tick_Carrier: submission.Cattle_Tick_Carrier,
    Movement_Originating: submission.Movement_Originating,
  }

  const mask = withinNswDestinationConditions(relevantSubmission)

  const allowedOptions = Object.keys(mask).filter(
    (key) =>
      mask[key as keyof ReturnType<typeof withinNswDestinationConditions>],
  )
  return options.filter((opt) => allowedOptions.includes(opt.value))
}

const propertyDestinationConditions = (submission: Submission) => {
  const restrictedPropertyOnly =
    nswCommunityVenue(submission) ||
    nswSaleyard(submission) ||
    nswNonRestrictedProperty(submission)
  return {
    'Moving onto an INFESTED holding in NSW': anywhereFromWithinNsw(submission),
    'Moving onto an ADJOINING holding in NSW':
      anywhereFromWithinNsw(submission),
    'Moving onto a non restricted property':
      anywhereFromWithinNsw(submission) && !restrictedPropertyOnly,
  }
}

export function filterPropertyDestinationOptions(
  options: FormTypes.ChoiceElementOption[],
  submission: Submission,
) {
  if (submission.Entering_NSW !== 'No') {
    return options
  }

  const relevantSubmission = {
    Movement_Originating: submission.Movement_Originating,
  }

  const mask = propertyDestinationConditions(relevantSubmission)

  const allowedOptions = Object.keys(mask).filter(
    (key) =>
      mask[key as keyof ReturnType<typeof propertyDestinationConditions>],
  )
  return options.filter((opt) => allowedOptions.includes(opt.value))
}

// entering NSW Movement_Originating
const enteringNSWMovementOriginatingConditions = memoize((submission) => {
  const horsesOnlyBool = horsesOnly(submission)

  return {
    'FROM the QLD infested zone': true,
    'FROM a non-restricted property in the QLD tick free zone': true,
    'FROM a restricted property in the QLD tick free zone': true,
    'FROM the Magic Millions Sales Complex': horsesOnlyBool,
    'FROM the Royal Brisbane Show (EKKA)': true,
  }
}, JSON.stringify)

function filterEnteringNSWMovementOriginatingOptions(
  options: FormTypes.ChoiceElementOption[],
  submission: Submission,
) {
  if (submission.Entering_NSW !== 'Yes') {
    return options
  }
  const relevantSubmission = {
    Entering_NSW: submission.Entering_NSW,
    Species: submission.Cattle_Tick_Carrier_Sub,
    Cattle_Tick_Carrier: submission.Cattle_Tick_Carrier,
  }

  const mask = enteringNSWMovementOriginatingConditions(relevantSubmission)

  const allowedOptions = Object.keys(mask).filter(
    (key) =>
      mask[
        key as keyof ReturnType<typeof enteringNSWMovementOriginatingConditions>
      ],
  )
  return options.filter((opt) => allowedOptions.includes(opt.value))
}

// within NSW Movement_Originating
const withinNSWMovementOriginatingConditions = memoize((submission) => {
  const notEquineBool = anySpecies(submission) && !equine(submission)

  return {
    'From an INFESTED holding in NSW': true,
    'From an ADJOINING holding in NSW': true,
    'From a NON-RESTRICTED PROPERTY in NSW': true,
    'From a community venue (e.g. showgrounds)': true,
    'From a saleyard': notEquineBool,
  }
}, JSON.stringify)

function filterWithinNSWMovementOriginatingOptions(
  options: FormTypes.ChoiceElementOption[],
  submission: Submission,
) {
  if (submission.Entering_NSW !== 'No') {
    return options
  }
  const relevantSubmission = {
    Entering_NSW: submission.Entering_NSW,
    Species: submission.Cattle_Tick_Carrier_Sub,
    Cattle_Tick_Carrier: submission.Cattle_Tick_Carrier,
  }

  const mask = withinNSWMovementOriginatingConditions(relevantSubmission)

  const allowedOptions = Object.keys(mask).filter(
    (key) =>
      mask[
        key as keyof ReturnType<typeof withinNSWMovementOriginatingConditions>
      ],
  )
  return options.filter((opt) => allowedOptions.includes(opt.value))
}

export function filterMovementOriginatingOptions(
  options: FormTypes.ChoiceElementOption[],
  submission: Submission,
) {
  const enteringNSW = submission.Entering_NSW as string
  if (!enteringNSW) {
    return options
  }

  const filteredOptions =
    enteringNSW.toLowerCase() === 'yes'
      ? filterEnteringNSWMovementOriginatingOptions(options, submission)
      : filterWithinNSWMovementOriginatingOptions(options, submission)

  return filteredOptions
}
